import { apiFetch } from "@/api/client";
import { ep } from "@/api/endpoints";
import type { PushTokenRequest } from "@/api/types";
import { onboardingStep } from "@/app/onboarding";
import { platform, type PushPermission } from "@/platform";
import { useAuthStore } from "@/stores/authStore";
import { usePushStore } from "@/stores/pushStore";

/**
 * 알림 권한·푸시 토큰 (AC0-260). 앱 전용 — 웹은 platform.push 가 늘 denied 라 아무것도 하지 않는다.
 * 소프트 애스크(F01-15)는 가입 직후 1회, OS 권한이 아직 "묻지 않음"일 때만. 토큰은 권한이 있고 설정에서 끄지 않았으면 회원 세션마다 서버에 올린다(upsert).
 */

const isNative = () => platform.clientKind !== "web";
const isMember = () => {
  const s = useAuthStore.getState();
  return s.status === "member" && onboardingStep(s.user) === null;
};

/** F01-15 를 보여 줄까: 앱 · 아직 안 물음 · OS 권한이 prompt */
export async function shouldOfferPushAsk(): Promise<boolean> {
  if (!isNative() || usePushStore.getState().asked) return false;
  return (await platform.push.permission()) === "prompt";
}

let inflight: Promise<void> | null = null;

/** 권한이 있고 끄지 않았으면 FCM 토큰을 받아 서버에 올린다. 실패는 조용히(다음 시작에 다시) */
export function syncPushToken(): Promise<void> {
  inflight ??= (async () => {
    if (!isNative() || !isMember() || usePushStore.getState().off) return;
    if ((await platform.push.permission()) !== "granted") return;
    const token = await platform.push.register();
    if (!token) return;
    const body: PushTokenRequest = { token, platform: platform.clientKind === "ios" ? "ios" : "android" };
    await apiFetch(ep.mePushTokens, { method: "POST", body });
    usePushStore.getState().setToken(token);
  })()
    .catch(() => undefined)
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** "알림 받기"·설정 켜기: OS 권한 요청(F01-16) → 허용이면 켬 + 토큰 등록. 결과 권한을 돌려준다 */
export async function enablePush(): Promise<PushPermission> {
  const current = await platform.push.permission();
  const result = current === "prompt" ? await platform.push.requestPermission() : current;
  if (result === "granted") {
    usePushStore.getState().setOff(false);
    await syncPushToken();
  }
  return result;
}

/** 설정 끄기: 이 기기 토큰을 서버에서 지운다(OS 권한은 그대로). 서버 삭제가 실패해도 끈 상태는 유지 — 다음 시작에 다시 올리지 않는다 */
export async function disablePush(): Promise<void> {
  const store = usePushStore.getState();
  store.setOff(true);
  const token = store.token;
  if (!token) return;
  try {
    await apiFetch(ep.mePushToken(token), { method: "DELETE" });
    usePushStore.getState().setToken(null);
  } catch {
    // 남은 토큰은 로그아웃·FCM 무효 응답 때 지워진다
  }
}

/** 알림 data → 이동할 채팅방 경로. 모르는 형태면 null */
export function pushTargetPath(data: Record<string, string>): string | null {
  if (data.type !== "reply" || !data.plotSlug) return null;
  return `/chat/${encodeURIComponent(data.plotSlug)}`;
}
